import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useCloseOnClickOutside } from '../../../hooks/useCloseOnClickOutside';
import { API_URLS } from '../../../config/Constants';
import api from '../../../config/axiosConfig';
import { sessionManager } from '../../../config/sessionManager';
import { showErrorMsg } from '../../../config/common';
import { ComprehensiveExportButton } from '../../../components/ComprehensiveExportButton';
import PlayersReportTable, { PlayerBase } from '../../../components/shared/PlayersReportTable';
import WomensPlayerTables, { buildWomensExportReports } from '../../../components/shared/WomensPlayerTables';
import PlayerNameButton from '../../../components/playerSheet/PlayerNameButton';
import {
  filterGamesByMode,
  formatLeaderboardGameLabel,
  GAME_MODE_PRACTICE,
  parseGameMode,
} from '../../../utils/seasonType';

// Player types for each section
// Note: Using imported PlayerBase from PlayersReportTable

interface PossessionPlayer extends PlayerBase {
  groundBalls: number;
  turnovers: number;
  causedTurnovers: number;
  shotsSaved: number;
  npc: number;
  goalsPlusMinus?: number; // Derived NPC/.25
}

interface OffensePlayer extends PlayerBase {
  shots: number;
  saved: number;
  goals: number;
  assists: number;
  points: number;
  shotPercentage: number;
  sogPercentage: number;
  missedShots: number;
}

interface GoaliePlayer extends PlayerBase {
  shotsOnGoal: number;
  saved: number;
  goalsAllowed: number;
}

interface FaceoffPlayer extends PlayerBase {
  faceoffs: number;
  won: number;
  lost: number;
  winPercentage: number;
  lossPercentage: number;
}

interface PenaltyPlayer extends PlayerBase {
  penalties: number;
}

interface BlockedShotsPlayer extends PlayerBase {
  blockedShots: number;
}

// Quarter Selector Component
const QuarterSelector: React.FC<{
  periods: string[];
  activeQuarter: string;
  onQuarterChange: (quarter: string) => void;
}> = ({ periods, activeQuarter, onQuarterChange }) => (
  <div className="flex gap-2 mb-6">
    {periods.map((q) => (
      <button
        key={q}
        className={`px-4 py-2 rounded font-semibold transition-colors ${
          activeQuarter === q ? 'bg-sd-navy text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        }`}
        onClick={() => onQuarterChange(q)}
      >
        {q}
      </button>
    ))}
  </div>
);

// Main Component
const PlayersPageWeb: React.FC = () => {
  const clientId = sessionManager.getClientId();
  const seasonId = sessionManager.getSeasonId();
  const gamesMode = parseGameMode(sessionManager.getGamesMode());

  // Team and season info from session storage
  const teamName = sessionManager.getClientName() || 'Team';
  const seasonName = sessionManager.getSeasonName() || 'Season';

  // Games + filter selection
  const [games, setGames] = useState<any[]>([]);
  const [selectedGameOption, setSelectedGameOption] = useState<string>('ALL'); // ALL | LEAGUE | gameId
  const [selectedCustomGameIds, setSelectedCustomGameIds] = useState<string[]>([]);
  const [gameFilterReady, setGameFilterReady] = useState<boolean>(false);
  const [isGameDropdownOpen, setIsGameDropdownOpen] = useState<boolean>(false);
  const gameDropdownRef = useRef<HTMLDivElement>(null);

  const [periods, setPeriods] = useState<string[]>([]);
  const [activeQuarter, setActiveQuarter] = useState('Totals');

  // Section data
  const [possessionPlayers, setPossessionPlayers] = useState<PossessionPlayer[]>([]);
  const [offensePlayers, setOffensePlayers] = useState<OffensePlayer[]>([]);
  const [goaliePlayers, setGoaliePlayers] = useState<GoaliePlayer[]>([]);
  const [faceoffPlayers, setFaceoffPlayers] = useState<FaceoffPlayer[]>([]);
  const [penaltyPlayers, setPenaltyPlayers] = useState<PenaltyPlayer[]>([]);
  const [rawPlayers, setRawPlayers] = useState<any[]>([]);
  const [blockedShotsPlayers, setBlockedShotsPlayers] = useState<BlockedShotsPlayer[]>([]);

  // Sort configurations for each table
  const [possessionSortConfig, setPossessionSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });
  const [offenseSortConfig, setOffenseSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });
  const [goalieSortConfig, setGoalieSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });
  const [faceoffSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });
  const [penaltySortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });
  const [blockedShotsSortConfig, setBlockedShotsSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });

  // Column definitions
  const possessionColumns = [
    {
      header: 'Player',
      key: 'special' as keyof PossessionPlayer,
      sortable: true,
      render: (p: PossessionPlayer) => (
        <div className="text-left">
          <div className="font-semibold">
            <PlayerNameButton playerSeasonId={p.playerId} seasonId={seasonId}>
              {p.playerNumber} {p.playerName}
            </PlayerNameButton>
          </div>
        </div>
      ),
    },
    {
      header: 'Position',
      key: 'special' as keyof PossessionPlayer,
      sortable: true,
      render: (p: PossessionPlayer) =>
        Array.isArray(p.position) ? (
          <div className="whitespace-nowrap">
            {p.position
              .map((pos) =>
                pos === 'Long Stick Midfield' ? 'LSM' : pos === 'Short Stick Defensive Midfield' ? 'SSDM' : pos,
              )
              .join(' | ')}
          </div>
        ) : (
          (p.position === 'Long Stick Midfield'
            ? 'LSM'
            : p.position === 'Short Stick Defensive Midfield'
              ? 'SSDM'
              : p.position) || '-'
        ),
    },
    {
      header: 'GP',
      key: 'special' as keyof PossessionPlayer,
      sortable: true,
      render: (p: PossessionPlayer) => p.gamesPlayed ?? 0,
    },
    {
      header: 'GB',
      key: 'groundBalls' as keyof PossessionPlayer,
      sortable: true,
    },
    {
      header: 'TO',
      key: 'turnovers' as keyof PossessionPlayer,
      sortable: true,
    },
    {
      header: 'TO/PG',
      key: 'special' as keyof PossessionPlayer,
      sortable: true,
      render: (player: PossessionPlayer) => {
        const gamesPlayed = player.gamesPlayed || 1;
        return (player.turnovers / gamesPlayed).toFixed(1);
      },
    },
    {
      header: 'CTO',
      key: 'causedTurnovers' as keyof PossessionPlayer,
      sortable: true,
    },
    {
      header: 'CTO/PG',
      key: 'special' as keyof PossessionPlayer,
      sortable: true,
      render: (player: PossessionPlayer) => {
        const gamesPlayed = player.gamesPlayed || 1;
        return (player.causedTurnovers / gamesPlayed).toFixed(1);
      },
    },
    {
      header: 'SS',
      key: 'shotsSaved' as keyof PossessionPlayer,
      sortable: true,
    },
    { header: 'NPC', key: 'npc' as keyof PossessionPlayer, sortable: true },
    {
      header: 'G+/-',
      key: 'goalsPlusMinus' as keyof PossessionPlayer,
      sortable: true,
    },
  ];

  const offenseColumns = [
    {
      header: 'Player',
      key: 'special' as keyof OffensePlayer,
      sortable: true,
      render: (p: OffensePlayer) => (
        <div className="text-left">
          <div className="font-semibold">
            <PlayerNameButton playerSeasonId={p.playerId} seasonId={seasonId}>
              {p.playerNumber} {p.playerName}
            </PlayerNameButton>
          </div>
        </div>
      ),
    },
    {
      header: 'Position',
      key: 'special' as keyof OffensePlayer,
      sortable: true,
      render: (p: OffensePlayer) =>
        Array.isArray(p.position) ? (
          <div className="whitespace-nowrap">
            {p.position
              .map((pos) =>
                pos === 'Long Stick Midfield' ? 'LSM' : pos === 'Short Stick Defensive Midfield' ? 'SSDM' : pos,
              )
              .join(' | ')}
          </div>
        ) : (
          (p.position === 'Long Stick Midfield'
            ? 'LSM'
            : p.position === 'Short Stick Defensive Midfield'
              ? 'SSDM'
              : p.position) || '-'
        ),
    },
    {
      header: 'GP',
      key: 'special' as keyof OffensePlayer,
      sortable: true,
      render: (p: OffensePlayer) => p.gamesPlayed ?? 0,
    },
    { header: 'S', key: 'shots' as keyof OffensePlayer, sortable: true },
    {
      header: 'Missed',
      key: 'missedShots' as keyof OffensePlayer,
      sortable: true,
    },
    { header: 'SS', key: 'saved' as keyof OffensePlayer, sortable: true },
    {
      header: 'G',
      key: 'goals' as keyof OffensePlayer,
      sortable: true,
    },
    {
      header: 'S%',
      key: 'shotPercentage' as keyof OffensePlayer,
      sortable: true,
      render: (player: OffensePlayer) => <div className="text-sm">{player.shotPercentage}%</div>,
    },
    {
      header: 'SOG%',
      key: 'sogPercentage' as keyof OffensePlayer,
      sortable: true,
      render: (player: OffensePlayer) => <div className="text-sm">{player.sogPercentage}%</div>,
    },
    {
      header: 'A',
      key: 'assists' as keyof OffensePlayer,
      sortable: true,
    },
    { header: 'P', key: 'points' as keyof OffensePlayer, sortable: true },
    {
      header: 'PPG',
      key: 'special' as keyof OffensePlayer,
      sortable: true,
      render: (player: OffensePlayer) => {
        const gamesPlayed = player.gamesPlayed || 1;
        return (player.points / gamesPlayed).toFixed(1);
      },
    },
    {
      header: 'GPG',
      key: 'special' as keyof OffensePlayer,
      sortable: true,
      render: (player: OffensePlayer) => {
        const gamesPlayed = player.gamesPlayed || 1;
        return (player.goals / gamesPlayed).toFixed(1);
      },
    },
  ];

  const goalieColumns = [
    {
      header: 'Player',
      key: 'special' as keyof GoaliePlayer,
      sortable: true,
      render: (p: GoaliePlayer) => (
        <div className="text-left">
          <div className="font-semibold">
            <PlayerNameButton playerSeasonId={p.playerId} seasonId={seasonId}>
              {p.playerNumber} {p.playerName}
            </PlayerNameButton>
          </div>
        </div>
      ),
    },
    {
      header: 'Position',
      key: 'special' as keyof GoaliePlayer,
      sortable: true,
      render: (p: GoaliePlayer) =>
        Array.isArray(p.position) ? (
          <div className="whitespace-nowrap">
            {p.position
              .map((pos) =>
                pos === 'Long Stick Midfield' ? 'LSM' : pos === 'Short Stick Defensive Midfield' ? 'SSDM' : pos,
              )
              .join(' | ')}
          </div>
        ) : (
          (p.position === 'Long Stick Midfield'
            ? 'LSM'
            : p.position === 'Short Stick Defensive Midfield'
              ? 'SSDM'
              : p.position) || 'G'
        ),
    },
    {
      header: 'GP',
      key: 'special' as keyof GoaliePlayer,
      sortable: true,
      render: (p: GoaliePlayer) => p.gamesPlayed ?? 0,
    },
    {
      header: 'SOG',
      key: 'shotsOnGoal' as keyof GoaliePlayer,
      sortable: true,
    },
    {
      header: 'SV',
      key: 'special' as keyof GoaliePlayer,
      sortable: true,
      render: (player: GoaliePlayer) => {
        const savePercentage =
          player.shotsOnGoal > 0 ? Number(((player.saved / player.shotsOnGoal) * 100).toFixed(0)) : 0;
        return (
          <div>
            <div>{player.saved}</div>
            <div className="text-xs text-gray-600">({savePercentage}%)</div>
          </div>
        );
      },
    },
    {
      header: 'SPG',
      key: 'special' as keyof GoaliePlayer,
      sortable: true,
      render: (player: GoaliePlayer) => {
        const gamesPlayed = player.gamesPlayed || 1;
        return (player.saved / gamesPlayed).toFixed(1);
      },
    },
    {
      header: 'GA',
      key: 'goalsAllowed' as keyof GoaliePlayer,
      sortable: true,
    },
    {
      header: 'GAPG',
      key: 'special' as keyof GoaliePlayer,
      sortable: true,
      render: (player: GoaliePlayer) => {
        const gamesPlayed = player.gamesPlayed || 1;
        return (player.goalsAllowed / gamesPlayed).toFixed(1);
      },
    },
  ];

  const faceoffColumns = [
    {
      header: 'Player',
      key: 'special' as keyof FaceoffPlayer,
      sortable: true,
      render: (p: FaceoffPlayer) => (
        <div className="text-left">
          <div className="font-semibold">
            <PlayerNameButton playerSeasonId={p.playerId} seasonId={seasonId}>
              {p.playerNumber} {p.playerName}
            </PlayerNameButton>
          </div>
        </div>
      ),
    },
    {
      header: 'Position',
      key: 'special' as keyof FaceoffPlayer,
      sortable: true,
      render: (p: FaceoffPlayer) =>
        Array.isArray(p.position) ? (
          <div className="whitespace-nowrap">
            {p.position
              .map((pos) =>
                pos === 'Long Stick Midfield' ? 'LSM' : pos === 'Short Stick Defensive Midfield' ? 'SSDM' : pos,
              )
              .join(' | ')}
          </div>
        ) : (
          (p.position === 'Long Stick Midfield'
            ? 'LSM'
            : p.position === 'Short Stick Defensive Midfield'
              ? 'SSDM'
              : p.position) || '-'
        ),
    },
    {
      header: 'GP',
      key: 'special' as keyof FaceoffPlayer,
      sortable: true,
      render: (p: FaceoffPlayer) => p.gamesPlayed ?? 0,
    },
    {
      header: 'DC',
      key: 'faceoffs' as keyof FaceoffPlayer,
      sortable: true,
    },
    {
      header: 'W',
      key: 'won' as keyof FaceoffPlayer,
      sortable: true,
    },
    {
      header: 'W%',
      key: 'winPercentage' as keyof FaceoffPlayer,
      sortable: true,
      render: (player: FaceoffPlayer) => {
        const winPercentage = player.faceoffs > 0 ? Number(((player.won / player.faceoffs) * 100).toFixed(0)) : 0;
        return <div>{winPercentage}%</div>;
      },
    },
    {
      header: 'L',
      key: 'lost' as keyof FaceoffPlayer,
      sortable: true,
    },
  ];

  const penaltyColumns = [
    {
      header: 'Player',
      key: 'special' as keyof PenaltyPlayer,
      sortable: true,
      render: (p: PenaltyPlayer) => (
        <div className="text-left">
          <div className="font-semibold">
            <PlayerNameButton playerSeasonId={p.playerId} seasonId={seasonId}>
              {p.playerNumber} {p.playerName}
            </PlayerNameButton>
          </div>
        </div>
      ),
    },
    {
      header: 'Position',
      key: 'special' as keyof PenaltyPlayer,
      sortable: true,
      render: (p: PenaltyPlayer) =>
        Array.isArray(p.position) ? (
          <div className="whitespace-nowrap">
            {p.position
              .map((pos) =>
                pos === 'Long Stick Midfield' ? 'LSM' : pos === 'Short Stick Defensive Midfield' ? 'SSDM' : pos,
              )
              .join(' | ')}
          </div>
        ) : (
          (p.position === 'Long Stick Midfield'
            ? 'LSM'
            : p.position === 'Short Stick Defensive Midfield'
              ? 'SSDM'
              : p.position) || '-'
        ),
    },
    {
      header: 'GP',
      key: 'special' as keyof PenaltyPlayer,
      sortable: true,
      render: (p: PenaltyPlayer) => p.gamesPlayed ?? 0,
    },
    {
      header: 'Penalties',
      key: 'penalties' as keyof PenaltyPlayer,
      sortable: true,
    },
  ];

  const blockedShotsColumns = [
    {
      header: 'Player',
      key: 'special' as keyof BlockedShotsPlayer,
      sortable: true,
      render: (p: BlockedShotsPlayer) => (
        <div className="text-left">
          <div className="font-semibold">
            <PlayerNameButton playerSeasonId={p.playerId} seasonId={seasonId}>
              {p.playerNumber} {p.playerName}
            </PlayerNameButton>
          </div>
        </div>
      ),
    },
    {
      header: 'Position',
      key: 'special' as keyof BlockedShotsPlayer,
      sortable: true,
      render: (p: BlockedShotsPlayer) =>
        Array.isArray(p.position) ? (
          <div className="whitespace-nowrap">
            {p.position
              .map((pos) =>
                pos === 'Long Stick Midfield' ? 'LSM' : pos === 'Short Stick Defensive Midfield' ? 'SSDM' : pos,
              )
              .join(' | ')}
          </div>
        ) : (
          (p.position === 'Long Stick Midfield'
            ? 'LSM'
            : p.position === 'Short Stick Defensive Midfield'
              ? 'SSDM'
              : p.position) || '-'
        ),
    },
    {
      header: 'GP',
      key: 'special' as keyof BlockedShotsPlayer,
      sortable: true,
      render: (p: BlockedShotsPlayer) => p.gamesPlayed ?? 0,
    },
    {
      header: 'Blocked Shots',
      key: 'blockedShots' as keyof BlockedShotsPlayer,
      sortable: true,
    },
  ];

  // Fetch periods on component mount
  useEffect(() => {
    fetchPeriods();
    fetchGames();
    const savedFilter = sessionManager.getSeasonGameFilter();
    if (savedFilter?.mode === 'league' && gamesMode !== GAME_MODE_PRACTICE) {
      setSelectedGameOption('LEAGUE');
      setSelectedCustomGameIds([]);
    } else if (savedFilter?.mode === 'custom') {
      setSelectedGameOption('CUSTOM');
      setSelectedCustomGameIds(savedFilter.customGameIds || []);
    } else if (savedFilter?.mode === 'single' && savedFilter.gameId) {
      setSelectedGameOption(savedFilter.gameId);
      setSelectedCustomGameIds([]);
    } else {
      setSelectedGameOption('ALL');
      setSelectedCustomGameIds([]);
    }
    setGameFilterReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch player stats when quarter changes
  useEffect(() => {
    if (periods.length > 0) {
      fetchPlayerStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuarter, periods, selectedGameOption, selectedCustomGameIds, gamesMode]);

  useEffect(() => {
    if (games.length === 0) return;
    const validIds = new Set(filterGamesByMode(games, gamesMode).map((g) => String(g._id)));
    setSelectedCustomGameIds((prev) => prev.filter((id) => validIds.has(String(id))));
    setSelectedGameOption((prev) => {
      if (gamesMode === GAME_MODE_PRACTICE && prev === 'LEAGUE') return 'ALL';
      if (prev === 'ALL' || prev === 'LEAGUE' || prev === 'CUSTOM') return prev;
      return validIds.has(String(prev)) ? prev : 'ALL';
    });
  }, [games, gamesMode]);

  useEffect(() => {
    if (!gameFilterReady) return;
    if (selectedGameOption === 'LEAGUE') {
      sessionManager.setSeasonGameFilter({ mode: 'league' });
      return;
    }
    if (selectedGameOption === 'CUSTOM') {
      sessionManager.setSeasonGameFilter({ mode: 'custom', customGameIds: selectedCustomGameIds });
      return;
    }
    if (selectedGameOption === 'ALL') {
      sessionManager.setSeasonGameFilter({ mode: 'all' });
      return;
    }
    if (selectedGameOption) {
      sessionManager.setSeasonGameFilter({ mode: 'single', gameId: selectedGameOption });
    }
  }, [gameFilterReady, selectedGameOption, selectedCustomGameIds]);

  useEffect(() => {
    if (gamesMode === GAME_MODE_PRACTICE && selectedGameOption === 'LEAGUE') {
      setSelectedGameOption('ALL');
      setSelectedCustomGameIds([]);
    }
  }, [gamesMode, selectedGameOption]);

  const selectableGames = React.useMemo(() => filterGamesByMode(games, gamesMode), [games, gamesMode]);
  const formatGameOptionLabel = useCallback(
    (game: any) => formatLeaderboardGameLabel(game, gamesMode),
    [gamesMode],
  );
  const selectedGameLabel = React.useMemo(() => {
    if (selectedCustomGameIds.length > 1) return `${selectedCustomGameIds.length} Games Selected`;
    if (selectedCustomGameIds.length === 1) {
      const game = selectableGames.find((g) => String(g._id) === selectedCustomGameIds[0]);
      return game ? formatGameOptionLabel(game) : '1 Game Selected';
    }
    if (selectedGameOption === 'LEAGUE') return 'League Games';
    if (selectedGameOption === 'ALL') return 'All Games';
    const game = selectableGames.find((g) => String(g._id) === selectedGameOption);
    return game ? formatGameOptionLabel(game) : 'All Games';
  }, [formatGameOptionLabel, selectableGames, selectedCustomGameIds, selectedGameOption]);
  const selectGamePreset = useCallback((value: 'ALL' | 'LEAGUE') => {
    setSelectedGameOption(value);
    setSelectedCustomGameIds([]);
    setIsGameDropdownOpen(false);
    if (value === 'LEAGUE') {
      sessionManager.setGameId('league');
      sessionManager.setGameName('League Games');
    } else {
      sessionManager.setGameId('');
      sessionManager.setGameName('');
    }
  }, []);
  const closeGameDropdown = useCallback(() => setIsGameDropdownOpen(false), []);
  useCloseOnClickOutside(gameDropdownRef, isGameDropdownOpen, closeGameDropdown);

  const toggleSelectedGame = useCallback((game: any) => {
    const gameId = String(game._id);
    setSelectedGameOption('CUSTOM');
    sessionManager.setGameId('');
    sessionManager.setGameName('Selected Games');
    setSelectedCustomGameIds((prev) =>
      prev.includes(gameId) ? prev.filter((id) => id !== gameId) : [...prev, gameId],
    );
  }, []);

  const fetchGames = async () => {
    if (!clientId || !seasonId) return;
    try {
      const resp = await api.get(`${API_URLS.GET_SEASON_GAMES}`, {
        params: { clientId, seasonId, gameMode: gamesMode },
      });
      if (resp.data.success) setGames(resp.data.data || []);
    } catch (e) {
      console.error('Failed to fetch games list', e);
    }
  };

  const fetchPeriods = async () => {
    try {
      const response = await api.get(`${API_URLS.GET_SEASON_PERIOD}`, {
        params: { clientId, seasonId },
      });

      if (response.data.success) {
        const hp = response.data.data.halfPeriod;
        let periodArr: string[] = [];

        if (hp === true) {
          periodArr = ['Totals', 'H1', 'H2'];
        } else {
          periodArr = ['Totals', 'Q1', 'Q2', 'Q3', 'Q4'];
        }

        // Add OT if any game has it
        if (response.data.data?.otFlag && !periodArr.includes('OT')) {
          periodArr.push('OT');
        }

        setPeriods(periodArr);
        setActiveQuarter('Totals');
      }
    } catch (error) {
      console.error('Failed to fetch periods:', error);
      setPeriods(['Totals', 'Q1', 'Q2', 'Q3', 'Q4']);
      setActiveQuarter('Totals');
    }
  };

  const fetchPlayerStats = useCallback(async () => {
    if (!clientId || !seasonId || !activeQuarter) return;
    if (selectedGameOption === 'CUSTOM' && selectedCustomGameIds.length === 0) {
      setPossessionPlayers([]);
      setOffensePlayers([]);
      setGoaliePlayers([]);
      setFaceoffPlayers([]);
      setPenaltyPlayers([]);
      setBlockedShotsPlayers([]);
      setRawPlayers([]);
      return;
    }

    const handlePlayersData = (playersData: any[]) => {
      processPossessionData(playersData);
      processOffenseData(playersData);
      processGoalieData(playersData);
      processFaceoffData(playersData);
      processPenaltyData(playersData);
      processBlockedShotsData(playersData);
      setRawPlayers(Array.isArray(playersData) ? playersData : []);
    };

    try {
      // Build unified URL. Backend handles league aggregation when league=true.
      const isLeague = selectedGameOption === 'LEAGUE';
      const isCustom = selectedGameOption === 'CUSTOM';
      const targetGameId = selectedGameOption !== 'ALL' && !isLeague && !isCustom ? selectedGameOption : null;
      const params: Record<string, string> = {
        clientId,
        seasonId,
        quarterId: activeQuarter,
        gameMode: gamesMode,
      };
      if (isLeague) params.league = 'true';
      else if (isCustom) params.gameIds = selectedCustomGameIds.join(',');
      else if (targetGameId) params.gameId = targetGameId;

      const response = await api.get(API_URLS.GET_PLAYER_STATS, { params });
      if (response.data.success) handlePlayersData(response.data.data);
      else showErrorMsg(response.data.message || 'Failed to fetch player statistics');
    } catch (err: any) {
      showErrorMsg(err.response?.data?.message || err.message || 'Failed to fetch player statistics');
    }
  }, [clientId, seasonId, activeQuarter, selectedGameOption, selectedCustomGameIds, gamesMode]);

  const processPossessionData = (playersData: any[]) => {
    const processed: PossessionPlayer[] = playersData
      .filter((player) => {
        // Exclude pure goalies from possession stats
        // A pure goalie is someone who only has goalie position and only goalie stats
        const positions = Array.isArray(player.position) ? player.position : [player.position].filter(Boolean);
        const isOnlyGoalie = positions.length === 1 && positions[0] === 'Goalie';

        // If they're only a goalie, exclude them unless they have non-goalie possession activities
        if (isOnlyGoalie) {
          const hasNonGoalieActivity =
            (player.groundballs || 0) > 0 || (player.turnovers || 0) > 0 || (player.causedturnover || 0) > 0;
          // Only include goalies if they have actual possession activities (not just saves)
          return hasNonGoalieActivity;
        }

        const activity = player.groundballs + player.turnovers + player.causedturnover + player.shotsaved;

        return !!activity;
      })
      .map((player) => {
        const groundBalls = player.groundballs || 0;
        const turnovers = player.turnovers || 0;
        const causedTurnovers = player.causedturnover || 0;
        // For possession stats, "shots saved" refers to offensive players who had shots saved against them
        // This is different from goalie saves
        const shotsSaved = player.shotsaved || 0;

        // NPC Definition: NPC = Ground Balls - Net Turnovers (TO - CTOs) - Shots Saved
        // Net Turnovers = Turnovers - Caused Turnovers
        const netTurnovers = turnovers - causedTurnovers;
        const npc = groundBalls - netTurnovers - shotsSaved;
        const goalsPlusMinus = npc * 0.25; // npc / 4

        return {
          playerId: player.playerSeasonId || player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          position: player.position || player.positions || [],
          gamesPlayed: player.gamesPlayed || player.gameCount || 0,
          groundBalls,
          turnovers,
          causedTurnovers,
          shotsSaved,
          npc,
          goalsPlusMinus,
        };
      });

    setPossessionPlayers(processed);
  };

  const processOffenseData = (playersData: any[]) => {
    const processed: OffensePlayer[] = playersData
      .filter((player) => {
        // Also check if they're a pure goalie - exclude them unless they took actual shots
        const positions = Array.isArray(player.position) ? player.position : [player.position].filter(Boolean);
        const isOnlyGoalie = positions.length === 1 && positions[0] === 'Goalie';

        if (isOnlyGoalie) {
          // Only include goalies in offense if they actually took shots (rare but possible)
          return player.shots > 0;
        }

        const activity = player.shots + player.shotsaved + player.shotmissed + player.goals + player.assist;

        return activity;
      })
      .map((player) => {
        const shots = player.shots || 0;
        // For offense, "saved" means this player's shots were saved by opponent goalie
        const saved = player.shotsaved || 0;
        const goals = player.goals || 0;
        const assists = player.assist || 0;
        const missedShots = player.shotmissed || 0;
        const points = goals + assists;
        const shotPercentage = shots > 0 ? Number(((goals / shots) * 100).toFixed(0)) : 0;
        const sogPercentage = shots > 0 ? Math.ceil(((goals + saved) / shots) * 100) : 0;

        return {
          playerId: player.playerSeasonId || player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          position: player.position || player.positions || [],
          gamesPlayed: player.gamesPlayed || player.gameCount || 0,
          shots,
          saved,
          goals,
          assists,
          points,
          shotPercentage,
          sogPercentage,
          missedShots,
        };
      });

    setOffensePlayers(processed);
  };

  const processGoalieData = (playersData: any[]) => {
    const processed: GoaliePlayer[] = playersData
      .filter((player) => player.shotsongoal)
      .map((player) => {
        // For goalies: saved = saves they made, shotsongoal = shots faced
        const shotsOnGoal = player.shotsongoal || 0;
        const saved = player.saved || 0;
        const goalsAllowed = player.goalsallowed || shotsOnGoal - saved;

        return {
          playerId: player.playerSeasonId || player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          position: player.position || player.positions || 'G',
          gamesPlayed: player.gamesPlayed || player.gameCount || 0,
          shotsOnGoal,
          saved,
          goalsAllowed,
        };
      });

    setGoaliePlayers(processed);
  };

  const processFaceoffData = (playersData: any[]) => {
    const processed: FaceoffPlayer[] = playersData
      .filter((player) => (player.faceoffwins || 0) > 0 || (player.faceofflosses || 0) > 0)
      .map((player) => {
        const won = player.faceoffwins || 0;
        const lost = player.faceofflosses || 0;
        const total = won + lost;
        const winPercentage = total > 0 ? Number(((won / total) * 100).toFixed(0)) : 0;
        const lossPercentage = total > 0 ? Number(((lost / total) * 100).toFixed(0)) : 0;

        return {
          playerId: player.playerSeasonId || player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          position: player.position || player.positions || [],
          gamesPlayed: player.gamesPlayed || player.gameCount || 0,
          faceoffs: total,
          won,
          lost,
          winPercentage,
          lossPercentage,
        };
      });

    setFaceoffPlayers(processed);
  };

  const processPenaltyData = (playersData: any[]) => {
    const processed: PenaltyPlayer[] = playersData
      .filter((player) => (player.penalties || 0) > 0)
      .map((player) => ({
        playerId: player.playerSeasonId || player._id,
        playerNumber: player.no,
        playerName: `${player.firstName} ${player.lastName}`,
        position: player.position || player.positions || [],
        gamesPlayed: player.gamesPlayed || player.gameCount || 0,
        penalties: player.penalties || 0,
      }));

    setPenaltyPlayers(processed);
  };

  const processBlockedShotsData = (playersData: any[]) => {
    const processed: BlockedShotsPlayer[] = playersData
      .filter((p) => (p.blocks || 0) > 0)
      .map((p) => ({
        playerId: p.playerSeasonId || p._id,
        playerNumber: p.no,
        playerName: `${p.firstName} ${p.lastName}`,
        position: p.position || p.positions || [],
        gamesPlayed: p.gamesPlayed || p.gameCount || 0,
        blockedShots: p.blocks || 0,
      }));
    setBlockedShotsPlayers(processed);
  };

  return (
    <div className="w-full mb-10 mt-6 px-4 sm:px-6 lg:px-8">
      <header className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-sd-navy">Dynamic Leaderboard</h1>
          </div>
          <p className="mt-2 text-sm sm:text-base text-sd-navy font-medium max-w-3xl">
            Individual player statistics by quarter (or totals). Use the refresh button to pull the latest numbers.
          </p>
        </div>
        <button
          className="bg-sd-accent w-10 h-10 rounded-md p-2 shadow-md cursor-pointer hover:bg-[#6fd315] transition-colors flex-shrink-0"
          onClick={fetchPlayerStats}
          title="Refresh Player Stats"
        >
          <img src="/icons/icon-refresh.png" alt="refresh" className="w-full h-full" />
        </button>
      </header>

      {/* Filters Row (align right like Teams page) */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        {/* Left: Quarter buttons */}
        <div className="flex-1 min-w-[240px]">
          <QuarterSelector periods={periods} activeQuarter={activeQuarter} onQuarterChange={setActiveQuarter} />
        </div>
        {/* Right: Game dropdown */}
        <div className="relative self-start w-full sm:w-72" ref={gameDropdownRef}>
          <button
            type="button"
            className="bg-white w-full text-sd-navy font-semibold px-3 py-2 rounded-md border border-sd-navy shadow-sm focus:outline-none pr-8 text-left truncate"
            onClick={() => setIsGameDropdownOpen((prev) => !prev)}
            aria-label="Games"
          >
            {selectedGameLabel}
          </button>
          <span className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="1" y="1" width="18" height="18" rx="4" stroke="rgb(var(--sd-navy))" strokeWidth="2" fill="rgb(var(--sd-navy))" />
              <path d="M7 8l3 3 3-3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          {isGameDropdownOpen && (
            <div className="absolute right-0 mt-1 z-50 w-full max-h-72 overflow-y-auto rounded-md border border-sd-line bg-white shadow-lg p-2">
              <button
                type="button"
                className="block w-full rounded-md px-3 py-2 min-h-10 text-left text-sm font-semibold text-sd-navy hover:bg-sd-light"
                onClick={() => selectGamePreset('ALL')}
              >
                All Games
              </button>
              {gamesMode !== GAME_MODE_PRACTICE && (
                <button
                  type="button"
                  className="block w-full rounded-md px-3 py-2 min-h-10 text-left text-sm font-semibold text-sd-navy hover:bg-sd-light"
                  onClick={() => selectGamePreset('LEAGUE')}
                >
                  League Games
                </button>
              )}
              <div className="my-1 border-t border-sd-line" />
              {selectableGames.map((g) => {
                const gid = String(g._id);
                const checked = selectedCustomGameIds.includes(gid);
                return (
                  <label
                    key={gid}
                    className="flex min-h-10 cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold text-sd-navy hover:bg-sd-light"
                  >
                    <input
                      type="checkbox"
                      className="h-[18px] w-[18px] shrink-0"
                      checked={checked}
                      onChange={() => toggleSelectedGame(g)}
                    />
                    <span className="truncate">{formatGameOptionLabel(g)}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Comprehensive Export Section */}
      <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-1">Export All Statistics</h3>
            <p className="text-sm text-gray-600">
              Download or email a comprehensive report containing all player statistics
            </p>
          </div>
          <ComprehensiveExportButton
            possessionPlayers={possessionPlayers}
            offensePlayers={offensePlayers}
            goaliePlayers={goaliePlayers}
            faceoffPlayers={faceoffPlayers}
            penaltyPlayers={penaltyPlayers}
            blockedShotsPlayers={blockedShotsPlayers}
            possessionColumns={possessionColumns}
            offenseColumns={offenseColumns}
            goalieColumns={goalieColumns}
            faceoffColumns={faceoffColumns}
            penaltyColumns={penaltyColumns}
            blockedShotsColumns={blockedShotsColumns}
            womensReports={buildWomensExportReports(rawPlayers)}
            teamName={teamName}
            seasonName={seasonName}
            clientId={clientId}
            sortConfigs={{
              possession: possessionSortConfig,
              offense: offenseSortConfig,
              goalie: goalieSortConfig,
              faceoff: faceoffSortConfig,
              penalty: penaltySortConfig,
              blockedShots: blockedShotsSortConfig,
            }}
          />
        </div>
      </div>

      {/* Player Statistics Tables */}
      <div className="border border-sd-line overflow-auto p-6 rounded-lg shadow-sm">
        <PlayersReportTable
          title="Possession"
          columns={possessionColumns}
          players={possessionPlayers}
          teamName={teamName}
          seasonName={seasonName}
          clientId={clientId}
          sortConfig={possessionSortConfig}
          onSortChange={setPossessionSortConfig}
          showExport={true}
        />

        <PlayersReportTable
          title="Offense"
          columns={offenseColumns}
          players={offensePlayers}
          teamName={teamName}
          seasonName={seasonName}
          clientId={clientId}
          sortConfig={offenseSortConfig}
          onSortChange={setOffenseSortConfig}
          showExport={true}
        />

        {/* Always render per requirements */}
        <PlayersReportTable
          title="Goalie"
          columns={goalieColumns}
          players={goaliePlayers}
          teamName={teamName}
          seasonName={seasonName}
          clientId={clientId}
          sortConfig={goalieSortConfig}
          onSortChange={setGoalieSortConfig}
          showExport={true}
        />

        <WomensPlayerTables
          playersData={rawPlayers}
          seasonId={seasonId}
          layout="leaderboard"
          renderTable={({ id, ...table }) => (
            <PlayersReportTable
              key={id}
              {...table}
              teamName={teamName}
              seasonName={seasonName}
              clientId={clientId}
              showExport={true}
            />
          )}
        />

        <PlayersReportTable
          title="Blocked Shots"
          columns={blockedShotsColumns}
          players={blockedShotsPlayers}
          teamName={teamName}
          seasonName={seasonName}
          clientId={clientId}
          sortConfig={blockedShotsSortConfig}
          onSortChange={setBlockedShotsSortConfig}
          showExport={true}
        />
      </div>
    </div>
  );
};

export default PlayersPageWeb;
